<script setup>
import { sessionExpired, clearSessionExpired } from "./sessionExpiry.js";

/**
 * What a lapsed session looks like.
 *
 * Before this, an expired session on a page that could not be redirected away
 * from produced nothing at all: every call 401'd, every catch set a local error
 * that most screens never rendered, and the visible result was buttons that had
 * simply stopped working. A dead session is not a per-screen error — it is a
 * fact about the whole app — so it is said once, over everything.
 *
 * Modal rather than a toast on purpose. Nothing behind it will work until the
 * user signs in again, so dismissing it into the corner would be dishonest
 * about the state they are in.
 */
const props = defineProps({
  /** Where "Sign in again" goes. Each plane has its own login route. */
  loginRoute: { type: Object, default: () => ({ name: "login" }) },
});

const emit = defineEmits(["signin"]);

function signIn() {
  clearSessionExpired();
  emit("signin", props.loginRoute);
}
</script>

<template>
  <Transition name="fade">
    <div
      v-if="sessionExpired"
      class="fixed inset-0 z-50 grid place-items-center p-4"
      style="background-color: rgb(0 0 0 / 0.6)"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div
        class="w-full max-w-sm rounded-xl p-5 space-y-3"
        style="background-color: rgb(var(--surface)); border: 1px solid rgb(var(--surface-border))"
      >
        <h2 id="session-expired-title" class="font-semibold" style="color: rgb(var(--content))">
          Your session has ended
        </h2>

        <p class="text-sm" style="color: rgb(var(--content-muted))">
          You were signed out — either the session expired or it was ended
          elsewhere. Sign in again to carry on. Nothing you were doing has been
          lost, but it will not be saved until you do.
        </p>

        <button class="btn-primary w-full" autofocus @click="signIn">Sign in again</button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
